<template>
  <v-dialog
    v-model="payInvoiceDialog"
    width="500"
    overlay-color="rgba(39, 50, 64)"
    overlay-opacity="0.6"
    :fullscreen="$vuetify.breakpoint.xs"
    persistent
    :transition="false"
  >
    <v-card>
      <v-card-title
        class="header"
        primary-title
      >
        <b>{{ $t('invoices.markAsPaid') }}</b>
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model.trim="form.amount"
          :value="invoice.total"
          class="mt-6"
          name="amount"
          maxlength="12"
          :label="$t('invoices.paidAmount')"
          :suffix="invoice.currency"
          :error-messages="AmountErrors"
          @input="$v.form.amount.$touch()"
          @blur="$v.form.amount.$touch()"
          @keypress="onlyForCurrency($event, form.amount)"
        />
        <v-checkbox
          v-model="form.from_balance"
          color="primary"
        >
          <template
            v-slot:label
          >
            <small>
              {{ $t('invoices.fromBalance') }}
            </small>
          </template>
        </v-checkbox>
        <v-layout v-if="form.from_balance && getUserBalance">
          <v-flex xs12>
            <p>
              {{ $t('invoices.user_balance') }}: <b>{{ getUserBalance.available }} {{ getUserBalance.currency }}</b>
            </p>
          </v-flex>
        </v-layout>
        <v-text-field
          v-if="form.from_balance"
          v-model.trim="form.amount_from_balance"
          name="amount"
          maxlength="12"
          :label="$t('invoices.debitedFromBalance')"
          :suffix="invoice.currency"
          :error-messages="AmountFromBalanceErrors"
          @input="$v.form.amount_from_balance.$touch()"
          @blur="$v.form.amount_from_balance.$touch()"
          @keypress="onlyForCurrency($event, form.amount_from_balance)"
        />
        <v-text-field
          v-model.trim="form.txn_id"
          class="mt-6 mb-5"
          name="txn-id"
          :label="$t('invoices.txnId')"
          :error-messages="TxnIdErrors"
          @input="$v.form.txn_id.$touch()"
          @blur="$v.form.txn_id.$touch()"
          @paste="$v.form.txn_id.$touch()"
        />
        <v-select
          v-model.trim="form.gateway"
          class="mt-3"
          :items="allowedGateways"
          :item-text="'name'"
          :item-value="'module'"
          :label="$t('invoices.selectGateway')"
          :error-messages="GatewayErrors"
          name="gateway"
          @input="$v.form.gateway.$touch()"
          @blur="$v.form.gateway.$touch()"
          @paste="$v.form.gateway.$touch()"
        />
      </v-card-text>
      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          :disabled="loading"
          @click="payInvoiceDialog = false"
        >
          <span>{{ $t('globals.cancelButton') }}</span>
        </v-btn>
        <v-btn
          color="primary"
          :disabled="loading || ($v.$invalid)"
          :loading="loading"
          @click="payInvoice()"
        >
          {{ $t('invoices.markAsPaid') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>


import PAY_INVOICE_MUTATION from "@/graphql/mutations/PayInvoice.gql";
import WL_GATEWAYS from "@/graphql/queries/whiteLabelGateways.gql";

import {validationMixin} from "vuelidate"
import {required, requiredIf} from "vuelidate/lib/validators"
import {mapGetters} from "vuex";

export default {
  name: "PayInvoiceDialog",
  mixins: [validationMixin],
  props: {
    invoice: {
      type: Object,
      default() {
        return null;
      }
    },
    showDialog: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      form: {
        id: this.invoice.id,
        txn_id: null,
        amount: this.invoice.total,
        gateway: null,
        from_balance: false,
        amount_from_balance: null,
      }
    };
  },
  validations() {
    return {
      form: this.rules
    };
  },
  computed: {
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
    allowedGateways() {
      const otherGateway = {module: "other", name: this.$t('invoices.other')};
      const gateways = (
          this.whiteLabelGateways
              ? this.whiteLabelGateways.filter(({status}) => status === "enabled")
              : []
      ).map((item) => ({
        ...item,
        name: item.display_name || item.name,
      }));
      return [...gateways, otherGateway]
    },
    payInvoiceDialog: {
      get() {
        return this.showDialog;
      },
      set() {
        this.$emit('closePayInvoiceDialog', false);
      }
    },
    getUserBalance() {
      return this.invoice ? this.invoice.user.wallet_balance.find(({currency}) => currency === this.invoice.currency) : null
    },
    rules() {
      return {
        txn_id: {
          required,
        },
        gateway: {
          required,
        },
        amount: {
          required,
        },
        amount_from_balance: {
          required: requiredIf(() => this.form.from_balance)
        }
      };
    },
    AmountErrors() {
      const errors = [];
      if (!this.$v.form.amount.$dirty) return errors;
      !this.$v.form.amount.required && errors.push(this.$t("projects.reqField"));
      return errors;
    },
    AmountFromBalanceErrors() {
      const errors = [];
      if (!this.$v.form.amount_from_balance.$dirty) return errors;
      !this.$v.form.amount_from_balance.required && errors.push(this.$t("projects.reqField"));
      return errors;
    },
    TxnIdErrors() {
      const errors = [];
      if (!this.$v.form.txn_id.$dirty) return errors;
      !this.$v.form.txn_id.required && errors.push(this.$t("projects.reqField"));
      return errors;
    },
    GatewayErrors() {
      const errors = [];
      if (!this.$v.form.gateway.$dirty) return errors;
      !this.$v.form.gateway.required && errors.push(this.$t("projects.reqField"));
      return errors;
    },
  },
  mounted() {
    if(this.invoice) {
      this.form.amount_from_balance =
        this.getUserBalance.available >= this.invoice.total
          ? this.invoice.total
          : this.getUserBalance.available;
    }
  },
  methods: {
    payInvoice() {
      let form = this.form;
      form.amount = parseFloat(this.form.amount);
      form.whitelabel_uuid = this.getWhiteLabel.uuid;

      if(form.from_balance) {
        form.amount_from_balance = parseFloat(this.form.amount_from_balance);
      }
      if(!form.from_balance) delete form.amount_from_balance;

      this.$apollo
          .mutate({
            mutation: PAY_INVOICE_MUTATION,
            variables: form
          })
          .then(() => {
            this.loading = false;
            this.$emit('closePayInvoiceDialog', true);
          })
          .catch(error => {
            this.loading = false;
            if (error.graphQLErrors[0].extensions.category == 'authorization' && error.graphQLErrors[0].message === "This action is unauthorized.") {
              this.$store.commit("app/setShowPermission");
              return;
            }
            this.errorHandler(error);
          });
    }
  },
  apollo: {
    whiteLabelGateways: {
      fetchPolicy: "network-only",
      query() {
        return WL_GATEWAYS;
      },
      variables() {
        {
          return {
            whitelabel_uuid: this.getWhiteLabel.uuid
          }
        }
      },
      skip() {
        return !this.getWhiteLabel;
      },
    },
  },
};
</script>

<style lang="scss">
</style>
